import type { Card } from '../../types'
import Icon from '../Common/Icon'

interface CardBackProps {
  card: Card
  wasCorrect: boolean
  onNext: () => void
}

function CardBack({ card, wasCorrect, onNext }: CardBackProps) {
  // concept 卡（无选项）只展示知识点，不显示胜负
  const isConceptCard = card.choices.length === 0

  return (
    <div className="p-4 relative">
      {/* ========== 答错全屏红色脉冲 (只触发一次, 0.6s 后淡出) ========== */}
      {!wasCorrect && !isConceptCard && (
        <div
          className="mistake-pulse-overlay"
          aria-hidden="true"
        />
      )}

      {/* ========== 结果横幅（仅 choice 卡显示） ========== */}
      {!isConceptCard && (
        <div
          className={`pixel-box p-3 mb-3 ${
            wasCorrect
              ? 'border-[color:var(--color-bull)] bg-[color:var(--color-bg-elevated)]'
              : 'border-[color:var(--color-bear)] bg-[color:var(--color-bg-elevated)]'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <span
              className="pixel-blink-x3 inline-flex"
              style={{ color: wasCorrect ? 'var(--color-bull)' : 'var(--color-bear)' }}
            >
              <Icon name={wasCorrect ? 'check' : 'cross'} size={24} />
            </span>
            <div>
              <div
                className="font-pixel text-xs tracking-wider"
                style={{ color: wasCorrect ? 'var(--color-bull)' : 'var(--color-bear)' }}
              >
                {wasCorrect ? '通关' : '答错了'}
              </div>
              <div className="font-pixel-body text-base text-[color:var(--color-text-bright)] mt-1">
                {wasCorrect ? '回答正确！' : '回答错误'}
              </div>
            </div>
          </div>
          <div className="font-pixel-body text-sm text-[color:var(--color-text-dim)] leading-snug">
            ► 正确答案：<span className="text-[color:var(--color-text-bright)]">{card.back.answer}</span>
          </div>
        </div>
      )}

      {/* ========== 概念卡提示横幅 ========== */}
      {isConceptCard && (
        <div className="pixel-box p-3 mb-3 border-[color:var(--color-cyan)] bg-[color:var(--color-bg-elevated)]">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="book" size={22} className="text-[color:var(--color-cyan)]" />
            <div>
              <div className="font-pixel text-xs tracking-wider text-[color:var(--color-cyan)]">
                核心要点
              </div>
              <div className="font-pixel-body text-base text-[color:var(--color-text-bright)] mt-1">
                {card.back.answer}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== 详细解释 ========== */}
      <div className="pixel-box bg-[color:var(--color-bg-elevated)] p-3 mb-3">
        <div className="font-pixel text-[10px] text-[color:var(--color-cyan)] mb-2 tracking-wider">
          ► 规则详解
        </div>
        <p className="font-pixel-body text-sm text-[color:var(--color-text)] leading-relaxed">
          {card.back.explanation}
        </p>
      </div>

      {/* ========== 常见错误 ========== */}
      {card.back.commonMistakes && card.back.commonMistakes.length > 0 && (
        <div className="pixel-box bg-[color:var(--color-bg-elevated)] p-3 mb-3 border-[color:var(--color-warning)]">
          <div className="font-pixel text-[10px] text-[color:var(--color-warning)] mb-2 tracking-wider inline-flex items-center gap-1.5">
            <Icon name="warn" size={11} />
            常见错误
          </div>
          <ul className="space-y-1 font-pixel-body text-sm text-[color:var(--color-text-dim)] leading-snug">
            {card.back.commonMistakes.map((mistake, index) => (
              <li key={index} className="flex gap-2">
                <span className="text-[color:var(--color-warning)] flex-shrink-0">▸</span>
                <span>{mistake}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ========== 知识点引用 ========== */}
      <div className="pixel-box bg-[color:var(--color-bg-elevated)] p-3 mb-3">
        <div className="font-pixel text-[10px] text-[color:var(--color-gold)] mb-2 tracking-wider">
          ► 关联概念
        </div>
        <div className="flex flex-wrap gap-2">
          {card.back.ruleRef.map((rule, index) => (
            <span
              key={index}
              className="pixel-tag text-[color:var(--color-gold)] border-[color:var(--color-gold)]"
            >
              {rule}
            </span>
          ))}
        </div>
      </div>

      {/* ========== 知识来源 ========== */}
      <div className="pixel-box bg-[color:var(--color-bg-elevated)] p-3 mb-4">
        <div className="font-pixel text-[10px] text-[color:var(--color-text-dim)] mb-1 tracking-wider">
          ► 知识来源
        </div>
        {card.back.sourceUrl ? (
          <a
            href={card.back.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-pixel-display text-[11px] text-[color:var(--color-cyan)] hover:text-[color:var(--color-bull)] leading-snug underline underline-offset-2 break-all"
          >
            <Icon name="page" size={11} className="align-[-1px]" /> {card.back.sourceRef} ↗
          </a>
        ) : (
          <p className="font-pixel-display text-[11px] text-[color:var(--color-text-dim)] leading-snug">
            <Icon name="page" size={11} className="align-[-1px]" /> {card.back.sourceRef}
          </p>
        )}
      </div>

      {/* ========== 下一张按钮 ========== */}
      <button onClick={onNext} className="pixel-button pixel-button-primary w-full">
        ► 下一题
      </button>
    </div>
  )
}

export default CardBack